import * as settings from "../../services/settings";
import * as warehouseService from "../../services/warehouse";

import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  Switch,
  Table,
  Typography,
  notification,
} from "antd";
import { useCallback, useEffect, useRef, useState } from "react";

import { ModalDelete } from "../../Components/model/modal/delete";
import { SearchOutlined } from "@ant-design/icons";
import permissions from "../../../configs/permission";
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../hooks/usePermission";
import { useTranslation } from "react-i18next";

const initPage = { limit: 0, page: 1 };
export const WarehousePage = () => {
  const { accessToken } = useAccessToken();
  const { hasPermission, isSuperuser } = usePermission();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(initPage);
  const [warehouses, setWarehouses] = useState([]);
  const [form] = Form.useForm();
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);
  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [updateWarehouses, setUpdateWarehouses] = useState();
  const { t } = useTranslation("warehouse", "buttons", "common");
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [prepareDeleteData, setPrepareDeleteData] = useState({});

  const { Title } = Typography;

  const openNotificationWithIcon = useCallback(
    (type, message, description) => {
      api[type]({ message, description });
    },
    [api]
  );

  const fetchWarehouse = useCallback(
    async (param) => {
      setLoading(true);
      try {
        const response = await warehouseService.getWarehouses(accessToken, param);
        setSearch({ ...param });
        setTotal(response.totalCount);
        setWarehouses(response.warehouse);
      } catch (err) {
        console.error("[WarehousePage] fetchWarehouse: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      }
      setLoading(false);
    },
    [accessToken]
  );

  const onFinish = useCallback(
    (value) => {
      const values = {
        warehouse_code: value["search"],
        warehouse_name: value["search"],
        warehouse_address: value["search"],
      };
      fetchWarehouse({ ...search, page: 1, values });
    },
    [search, fetchWarehouse]
  );

  const items = [
    { title: t("Page_Body_Tab_Menu.Home", { ns: "menus" }) },
    { title: t("Page_Body_Tab_Menu.Menu", { ns: "menus" }) },
    {
      title: (
        <a href="/warehouses" style={{ fontWeight: 600, color: "black" }}>
          {t("Title_Warehouse")}
        </a>
      ),
    },
  ];

  const columns = [
    {
      title: t("Warehouse_Table.Table_Code"),
      dataIndex: "warehouse_code",
    },
    {
      title: t("Warehouse_Table.Table_Name"),
      dataIndex: "warehouse_name",
    },
    {
      title: t("Warehouse_Table.Table_Warehouse_Address"),
      dataIndex: "warehouse_address",
    },
    {
      title: t("Warehouse_Table.Table_Is_Origin"),
      dataIndex: "is_origin",
      render: (_, record) => (
        <p style={{ color: record.is_origin ? "green" : "red" }}>
          <CloseCircleOutlined />
        </p>
      ),
    },
    {
      title: t("Warehouse_Table.Table_Is_Destination"),
      dataIndex: "is_destination",
      render: (_, record) => (
        <p style={{ color: record.is_destination ? "green" : "red" }}>
          <CheckCircleOutlined />
        </p>
      ),
    },
    {
      title: t("Warehouse_Table.Table_Is_Active"),
      dataIndex: "is_active",
      render: (_, record) => (
        <Switch
          disabled={!hasPermission(permissions.Warehouse.CHANGE)}
          value={record.is_active}
          onClick={(event) => {
            onChangeStatusWarehouse(record, event);
          }}
        />
      ),
    },
    {
      title: t("Edit", { ns: "common" }),
      dataIndex: "edit",
      render: (_, record) => (
        <Button
          type="link"
          disabled={!hasPermission(permissions.Warehouse.CHANGE)}
          onClick={() => {
            navigate("/warehouses/create", {
              state: { type: "EDIT", value: record },
            });
          }}
        >
          <EditOutlined />
        </Button>
      ),
    },
    {
      title: t("Delete", { ns: "common" }),
      dataIndex: "delete",
      render: (_, record) => (
        <Button
          type="link"
          disabled={!hasPermission(permissions.Warehouse.DELETE)}
          onClick={() => {
            setIsOpenDelete(true);
            setPrepareDeleteData(record);
          }}
        >
          <DeleteOutlined />
        </Button>
      ),
    },
  ];

  const onChangeStatusWarehouse = (value, event) => {
    value.is_active = event;
    setUpdateWarehouses(value);
    setOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      const response = await warehouseService.deleteWarehouse(accessToken, prepareDeleteData);
      if (response == undefined) {
        openNotificationWithIcon(
          settings.NotificationType.ERROR,
          <>{"Fail."}</>,
          <>{"Delete warehouse is error, Please check service"}</>
        );
        return;
      } else {
        openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{"Delete Success"}</>);
      }
      setPrepareDeleteData({});
      setIsOpenDelete(false);
      fetchWarehouse({ ...search });
      setLoading(false);
    } catch (err) {
      console.error("[WarehousePage] handleConfirmDelete: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      setLoading(false);
    }
    setOpenDeleteModal(false);
  };

  const handleOk = () => {
    updateDataWarehouse();
    setOpen(false);
  };

  const updateDataWarehouse = async () => {
    setLoading(true);
    try {
      const response = await warehouseService.updateWarehouse(accessToken, updateWarehouses);

      if (response == undefined) {
        openNotificationWithIcon(
          settings.NotificationType.ERROR,
          <>{"Fail."}</>,
          <>{"Update warehouse is error, Please check service"}</>
        );
        return;
      } else {
        openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{"Update Success"}</>);
      }

      fetchWarehouse({ ...search });
      setLoading(false);
    } catch (err) {
      console.error("[WarehousePage] updateDataWarehouse: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setUpdateWarehouses();
    fetchWarehouse({ ...search });
    setOpen(false);
    setOpenDeleteModal(false);
  };

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!accessToken || hasInitialized.current) return;
    hasInitialized.current = true;
    fetchWarehouse(search);
  }, [accessToken, fetchWarehouse, search]);

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row flex={1}>
          <Col span={24}>
            <Breadcrumb items={items} />
            <Row style={{ padding: "15px 0px" }}>
              <Col span={12}>
                <Title
                  level={4}
                  style={{
                    textWrap: "nowrap",
                    display: "flex",
                    gap: "7px",
                  }}
                >
                  <ArrowLeftOutlined style={{ fontSize: "20px" }} />
                  {t("Title_Warehouse")}
                </Title>
              </Col>
            </Row>
            <Form
              layout="inline"
              form={form}
              onFinish={onFinish}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  form.submit();
                }
              }}
              style={{ flexWrap: "nowrap", overflowX: "auto", minWidth: 280 }}
            >
              <Col span={21} style={{ minWidth: 200 }}>
                <Form.Item name="search">
                  <Input
                    size="default"
                    placeholder={t("Search", { ns: "common" })}
                    prefix={<SearchOutlined />}
                    style={{ minWidth: 200 }}
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Button style={{ width: "100%" }} type="primary" icon={<SearchOutlined />} htmlType="submit">
                  {t("BTN_Search", { ns: "buttons" })}
                </Button>
              </Col>
            </Form>
          </Col>
        </Row>
        <Row align="middle" justify="center" className="mt-4">
          <Col
            span={24}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              margin: "1rem",
            }}
          >
            {hasPermission(permissions.Warehouse.ADD) && (
              <Button
                className="flex justify-end"
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  navigate("/warehouses/create", {
                    state: { type: "CREATE" },
                  });
                }}
              >
                {t("Button_Warehouse.BTN_Add_Warehouse")}
              </Button>
            )}
          </Col>
          <Col span={24}>
            <Card className="mt-5">
              <Table
                loading={loading}
                columns={columns}
                dataSource={warehouses}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
                pagination={{
                  defaultCurrent: search.limit,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  current: search.page,
                  total: total,
                  onChange: (page, limit) => {
                    fetchWarehouse({ ...search, page, limit });
                  },
                  showSizeChanger: true,
                }}
                style={{ cursor: "pointer" }}
              />
            </Card>
          </Col>
        </Row>

        <Modal
          title="Confirm"
          open={isOpen}
          className="rounded-lg border-2"
          closable={false}
          centered={true}
          forceRender={true}
          footer={() => (
            <>
              <Button type="primary" onClick={handleOk}>
                {t("BTN_Save", { ns: "buttons" })}
              </Button>
              <Button onClick={handleCancel}>{t("BTN_Cancel", { ns: "buttons" })}</Button>
            </>
          )}
        >
          <h1>{"Confirm for update data"}</h1>
        </Modal>
        <ModalDelete
          isOpen={isOpenDelete}
          handleCloseModal={() => {
            setIsOpenDelete(false);
          }}
          onClick={() => {
            handleConfirmDelete(prepareDeleteData);
          }}
          dataDetail={prepareDeleteData}
          t={t}
        />
      </Spin>
    </>
  );
};
